import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { 
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  PaperClipIcon,
  ChatBubbleLeftIcon
} from '@heroicons/react/24/outline'

interface TicketUpdate {
  id: string
  userType: 'customer' | 'staff'
  userName: string
  message: string
  internal: boolean
  createdAt: string
}

interface TicketDetail {
  id: string
  title: string
  description: string
  category: string
  priority: string
  status: string
  createdAt: string
  updatedAt: string
  slaDueAt?: string
  assignedTo?: string
  updates: TicketUpdate[]
}

const TicketDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const [ticket, setTicket] = useState<TicketDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [newMessage, setNewMessage] = useState('')
  const [sendingMessage, setSendingMessage] = useState(false)

  useEffect(() => {
    if (id) {
      fetchTicketDetail(id)
    }
  }, [id])

  const fetchTicketDetail = async (ticketId: string) => {
    try {
      // Mock data for now - will be replaced with real API calls
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      const mockTicket: TicketDetail = {
        id: ticketId,
        title: 'Cannot access SharePoint Online',
        description: 'Getting error message when trying to access company SharePoint site. Error: Access Denied. This issue started yesterday after the weekend. It was working fine on Friday.',
        category: 'technical',
        priority: 'high',
        status: 'in_progress',
        createdAt: '2025-01-13T11:45:00Z',
        updatedAt: '2025-01-15T08:30:00Z',
        slaDueAt: '2025-01-15T15:45:00Z',
        assignedTo: 'Technical Support Team',
        updates: [
          {
            id: 'update_001',
            userType: 'customer',
            userName: 'Demo End User',
            message: 'This issue started yesterday after the weekend. It was working fine on Friday.',
            internal: false,
            createdAt: '2025-01-13T12:00:00Z'
          },
          {
            id: 'update_002',
            userType: 'staff',
            userName: 'VCNS Support',
            message: 'Thank you for reporting this issue. We are investigating the SharePoint permissions and will update you within 2 hours.',
            internal: false,
            createdAt: '2025-01-13T12:15:00Z'
          },
          {
            id: 'update_003',
            userType: 'staff',
            userName: 'Technical Team',
            message: 'Checked with Microsoft 365 admin center. The user permissions look correct. Investigating potential service issues.',
            internal: false,
            createdAt: '2025-01-14T09:30:00Z'
          },
          {
            id: 'update_004',
            userType: 'staff',
            userName: 'Technical Team',
            message: 'Found the issue - SharePoint site permissions were modified during weekend maintenance. Restoring access now.',
            internal: false,
            createdAt: '2025-01-15T08:30:00Z'
          }
        ]
      }
      
      setTicket(mockTicket)
    } catch (error) {
      console.error('Failed to fetch ticket detail:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newMessage.trim() || !ticket) return

    setSendingMessage(true)
    try {
      // Mock API call - will be replaced with real API
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      const newUpdate: TicketUpdate = {
        id: `update_${Date.now()}`,
        userType: 'customer',
        userName: 'Demo User',
        message: newMessage,
        internal: false,
        createdAt: new Date().toISOString()
      }
      
      setTicket({
        ...ticket,
        updates: [...ticket.updates, newUpdate],
        updatedAt: new Date().toISOString()
      })
      
      setNewMessage('')
    } catch (error) {
      console.error('Failed to send message:', error)
    } finally {
      setSendingMessage(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (status: string) => {
    const badges = {
      open: { class: 'status-pending', label: 'Open' },
      in_progress: { class: 'status-badge bg-vcns-accent bg-opacity-10 text-vcns-accent', label: 'In Progress' },
      waiting_customer: { class: 'status-badge bg-yellow-100 text-yellow-800', label: 'Waiting for Customer' },
      resolved: { class: 'status-active', label: 'Resolved' },
      closed: { class: 'status-inactive', label: 'Closed' }
    }
    const badge = badges[status as keyof typeof badges] || badges.open
    return { class: badge.class, label: badge.label }
  }

  const getPriorityBadge = (priority: string) => {
    const badges = {
      low: 'priority-low',
      medium: 'priority-medium',
      high: 'priority-high',
      urgent: 'priority-urgent'
    }
    return badges[priority as keyof typeof badges] || badges.medium
  }

  const getCategoryIcon = (category: string) => {
    const icons = {
      billing: '💰',
      technical: '🔧',
      provisioning: '📦',
      admin_access: '🔐'
    }
    return icons[category as keyof typeof icons] || '📋'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vcns-accent"></div>
      </div>
    )
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">Ticket not found</h2>
        <p className="mt-2 text-gray-600">The ticket you're looking for doesn't exist.</p>
      </div>
    )
  }

  const statusBadge = getStatusBadge(ticket.status)
  const isOverdue = ticket.slaDueAt && new Date(ticket.slaDueAt) < new Date()

  return (
    <>
      <Helmet>
        <title>Ticket #{ticket.id.split('_')[1]} - VCNS Support Portal</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-3 mb-2">
                  <span className="text-2xl">{getCategoryIcon(ticket.category)}</span>
                  <h1 className="text-2xl font-bold text-gray-900">{ticket.title}</h1>
                </div>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <span>Ticket #{ticket.id.split('_')[1]}</span>
                  <span className={statusBadge.class}>{statusBadge.label}</span>
                  <span className={`${getPriorityBadge(ticket.priority)} capitalize`}>
                    {ticket.priority} priority
                  </span>
                </div>
              </div>
              {isOverdue && (
                <div className="flex items-center text-red-600">
                  <ExclamationTriangleIcon className="w-5 h-5 mr-1" />
                  <span className="text-sm font-medium">SLA Overdue</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Ticket Details */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Description</h3>
              </div>
              <div className="px-6 py-4">
                <p className="text-gray-700 whitespace-pre-wrap">{ticket.description}</p>
              </div>
            </div>

            {/* Updates/Comments */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Updates ({ticket.updates.length})
                </h3>
              </div>
              <div className="px-6 py-4">
                <div className="space-y-4">
                  {ticket.updates.map((update) => (
                    <div key={update.id} className="flex space-x-3">
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                        update.userType === 'staff' ? 'bg-vcns-accent' : 'bg-gray-600'
                      }`}>
                        {update.userName.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">{update.userName}</span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            update.userType === 'staff' 
                              ? 'bg-vcns-accent bg-opacity-10 text-vcns-accent' 
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {update.userType === 'staff' ? 'VCNS Staff' : 'Customer'}
                          </span>
                          <span className="text-xs text-gray-500">{formatDate(update.createdAt)}</span>
                        </div>
                        <div className="mt-1 text-sm text-gray-700 bg-gray-50 rounded-lg p-3">
                          {update.message}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Add Comment */}
            {ticket.status !== 'closed' && (
              <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Add Update</h3>
                </div>
                <form onSubmit={handleSendMessage} className="px-6 py-4">
                  <div className="mb-4">
                    <textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      rows={4}
                      className="form-input"
                      placeholder="Type your message..."
                      required
                    />
                  </div>
                  <div className="flex justify-end">
                    <button 
                      type="submit" 
                      disabled={sendingMessage || !newMessage.trim()}
                      className="btn-primary"
                    >
                      {sendingMessage ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Sending...
                        </div>
                      ) : (
                        <>
                          <ChatBubbleLeftIcon className="w-4 h-4 mr-2" />
                          Send Update
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status & Info */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Ticket Information</h3>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1">
                    <span className={statusBadge.class}>{statusBadge.label}</span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Priority</dt>
                  <dd className="mt-1">
                    <span className={`${getPriorityBadge(ticket.priority)} capitalize`}>
                      {ticket.priority}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Category</dt>
                  <dd className="mt-1 text-sm text-gray-900 capitalize">{ticket.category.replace('_', ' ')}</dd>
                </div>
                {ticket.assignedTo && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Assigned To</dt>
                    <dd className="mt-1 text-sm text-gray-900">{ticket.assignedTo}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(ticket.createdAt)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(ticket.updatedAt)}</dd>
                </div>
                {ticket.slaDueAt && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">SLA Due</dt>
                    <dd className={`mt-1 text-sm flex items-center ${isOverdue ? 'text-red-600' : 'text-gray-900'}`}>
                      <ClockIcon className="w-4 h-4 mr-1" />
                      {formatDate(ticket.slaDueAt)}
                      {isOverdue && <span className="ml-2 text-xs">(Overdue)</span>}
                    </dd>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            {ticket.status === 'resolved' && (
              <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Actions</h3>
                </div>
                <div className="px-6 py-4">
                  <div className="space-y-3">
                    <button className="w-full btn-success">
                      <CheckCircleIcon className="w-4 h-4 mr-2" />
                      Mark as Closed
                    </button>
                    <button className="w-full btn-outline">
                      Reopen Ticket
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default TicketDetail
